import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { eventsApi } from '../../lib/api';

function InventorFeeSection({ event }) {
  const [agreed, setAgreed] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const feeAmount = (event.inventor_fee_cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const handleCheckout = async () => {
    if (!agreed || !email) return;
    setLoading(true);
    try {
      const { url } = await eventsApi.checkoutInventorFee(event.pitch_event_id || event.id, {
        inventor_name: name,
        inventor_email: email,
      });
      window.location.href = url;
    } catch (err) {
      alert('Checkout failed: ' + err.message);
      setLoading(false);
    }
  };

  return (
    <div className="border-2 border-gray-200 rounded-2xl p-8 mt-10">
      <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>
        {event.inventor_fee_label || 'Application Fee'}
      </h2>

      {event.inventor_fee_description && (
        <p className="text-sm text-gray-600 leading-relaxed mb-6">{event.inventor_fee_description}</p>
      )}

      <div className="bg-gray-50 rounded-xl p-5 mb-6 text-center">
        <div className="text-3xl font-bold text-gray-900 mb-1">{feeAmount}</div>
        <div className="text-sm text-gray-500">Application Fee</div>
      </div>

      <div className="space-y-4 mb-6">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Your Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="First & Last Name"
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-sm focus:border-amber-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Your Email *</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 text-sm focus:border-amber-500 focus:outline-none" />
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
        <p className="text-sm text-amber-800 font-medium">
          This fee is non-refundable regardless of whether your application is accepted.
        </p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer mb-6">
        <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)}
          className="mt-1 w-4 h-4 accent-amber-500" />
        <span className="text-sm text-gray-600">
          I understand this application fee is non-refundable.
        </span>
      </label>

      <button
        onClick={handleCheckout}
        disabled={!agreed || !email || loading}
        className={`block w-full text-center px-10 py-4 rounded-full text-base font-semibold text-white transition-all ${
          agreed && email && !loading ? 'hover:shadow-lg' : 'opacity-50 cursor-not-allowed'
        }`}
        style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
      >
        {loading ? 'Redirecting to checkout...' : `Pay ${feeAmount} & Continue →`}
      </button>
    </div>
  );
}

export default function EventInventorPage({ slug }) {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from('event_pages')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'published')
        .single();
      setEvent(data);
      setLoading(false);
    };
    load();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-gray-400 text-sm">Loading...</div>
    </div>
  );

  if (!event) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Event Not Found</h1>
        <p className="text-gray-500">This event doesn't exist or hasn't been published yet.</p>
      </div>
    </div>
  );

  const formatDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          {event.logo_image_url && <img src={event.logo_image_url} alt="" className="h-12 mx-auto mb-6" />}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>
            {event.event_name}
          </h1>
          <div className="flex items-center justify-center gap-6 text-gray-300 text-sm mb-8">
            {event.start_date && (
              <span>📅 {formatDate(event.start_date)}{event.end_date ? ` — ${formatDate(event.end_date)}` : ''}</span>
            )}
            {event.time_commitment && <span>⏱ {event.time_commitment}</span>}
          </div>
        </div>
      </section>

      {/* Acceptance Message */}
      <section className="max-w-3xl mx-auto px-6 py-16">
        {event.inventor_acceptance_msg && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 mb-10">
            <div className="text-emerald-700 text-lg leading-relaxed whitespace-pre-line">{event.inventor_acceptance_msg}</div>
          </div>
        )}

        {/* Instructions */}
        {event.inventor_instructions && (
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'EB Garamond, Georgia, serif' }}>What to Prepare</h2>
            <div className="text-gray-600 leading-relaxed whitespace-pre-line">{event.inventor_instructions}</div>
          </div>
        )}

        {/* Inventor Fee */}
        {event.inventor_fee_enabled && (
          <InventorFeeSection event={event} />
        )}

        {/* CTA (only shown if no fee — fee section has its own CTA) */}
        {!event.inventor_fee_enabled && event.inventor_cta_text && event.inventor_cta_link && (
          <div className="text-center mt-12">
            <a
              href={event.inventor_cta_link}
              target="_blank"
              rel="noreferrer"
              className="inline-block px-10 py-4 rounded-full text-base font-semibold text-white transition-all hover:shadow-lg"
              style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
            >
              {event.inventor_cta_text}
            </a>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 text-center">
        <p className="text-xs text-gray-400">
          Powered by <span className="font-semibold" style={{ color: '#c9a84c' }}>PlayVentr</span>
        </p>
      </footer>
    </div>
  );
}
